/**
 * Decide whether a patient-search term is a phone-number query, and derive the same
 * comparison key the backend derives.
 *
 * ⚠️ DUPLICATED RULE. This is a deliberate second implementation of
 * `authentication/app/Support/PhoneSearchKey.php` (`MIN_DIGITS = 4`), which is the
 * authoritative one — the backend decides what actually matches. The copy exists because
 * the frontend needs the same answer *before* it issues the request, to suppress the
 * "Create new patient" affordance on a phone term (see GeneralTab). If the backend
 * threshold or the leading-prefix rule ever changes, this file is the second place to
 * change, and `phoneSearch.test.ts` is the third. Parent ticket: 86d3nzmg1.
 *
 * The rule, in order:
 *   1. strip every non-digit — so `(03) 9123 4567`, `+61 3 9123 4567` and `0391234567`
 *      all reduce to the same digits;
 *   2. trim ONE leading `61` (country code) or `0` (national trunk prefix) — never both,
 *      never repeatedly;
 *   3. the term is a phone query only if at least PHONE_SEARCH_MIN_DIGITS digits remain.
 *
 * Step 2 happens BEFORE the length gate, which is the part that is easy to get wrong:
 * `0412` is four typed digits but only three after the trunk prefix is trimmed, so it is
 * NOT a phone query.
 *
 * The rule deliberately accepts some noise — a four-digit year like `1990` reads as a
 * phone query. That is the accepted trade-off for matching a short number fragment; the
 * cost is only that the create-new-patient shortcut is withheld for that term.
 */

/** Minimum digits remaining after the leading-prefix trim for a term to count as a phone query. */
export const PHONE_SEARCH_MIN_DIGITS = 4;

/**
 * The normalised digits a phone query should be matched on, or `null` when the term is
 * not a phone query at all (too few digits, or no digits — e.g. a name).
 */
export const phoneSearchKey = (term: string): string | null => {
	const digits = (term ?? "").replace(/\D/g, "");

	let trimmed = digits;
	if (trimmed.startsWith("61")) {
		trimmed = trimmed.slice(2);
	} else if (trimmed.startsWith("0")) {
		trimmed = trimmed.slice(1);
	}

	return trimmed.length >= PHONE_SEARCH_MIN_DIGITS ? trimmed : null;
};

/** True when the typed term should be treated as a phone-number search. */
export const isPhoneSearchTerm = (term: string): boolean => phoneSearchKey(term) !== null;
